import React, { useState, useMemo } from "react";
import getConfig from "next/config";
import { arrayOf, bool, func, object, string, number } from "prop-types";
import { Trans, useTranslation } from "@lib/i18n";

import Typography from "@components/shared/Typography";
import SimpleButton from "@components/shared/SimpleButton";
import ArrowRightIcon from "@components/shared/Icons/ArrowRight";
import LegalMentionModal from "./LegalMentionModal";
import AnimatedTuneIcon from "@components/shared/Icons/AnimatedTune";

import FinanceDisplayBlocks, {
  getDisplayBlocksVariant,
} from "@components/Financing/FinanceDisplayBlocks";

import { useOpenFinanceWidget } from "@hooks/useOpenFinanceWidget";
import { useIsB2BSelected } from "@hooks/useIsB2BSelected";
import { useGetFormattedPrice } from "@hooks/useGetFormattedPrice";
import { useEcoBonusSelector } from "@redux/reducers/appConfig";
import { getIsVACJourneySelected } from "@shared/v2/lib/getIsVACJourneySelected";
import { getParsedLegalMention } from "@shared/v2/lib/getParsedLegalMention";
import getPriceAlgorithm from "@shared/PriceAlgorithm/getPriceAlgorithm";
import { dealType } from "types";

const { publicRuntimeConfig } = getConfig();

const { COUNTRY } = publicRuntimeConfig;

const FinanceFinancing = ({
  shouldDisplayError,
  financialProductLabel,
  displayBlocks,
  legalMention,
  customizeButtonText,
  journey,
  isWidgetEditable,
  isModifiable,
  onModifyButtonClick,
  withDeliveryInfo,
  isQuantityVisible,
  vehicleQuantity,
  isReadOnly,
  deal,
}) => {
  const { t } = useTranslation();
  const openFinanceWidget = useOpenFinanceWidget();
  const ecoBonus = useEcoBonusSelector();
  const priceAlgorithm = getPriceAlgorithm(COUNTRY);
  const getFormattedPrice = useGetFormattedPrice();
  const isB2BSelected = useIsB2BSelected();

  const shouldDisplayModalLink = !getIsVACJourneySelected(journey);

  const parsedLegalMentions = useMemo(() => {
    return getParsedLegalMention(legalMention);
  }, [legalMention]);

  const [isLegalModalOpen, setIsLegalModalOpen] = useState(false);

  return (
    <>
      {shouldDisplayError ? (
        <Typography className="flex justify-center items-center text-black text-center font-semibold min-h-40">
          {t("financeDataErrorMessage")}
        </Typography>
      ) : (
        <>
          <Typography className="text-lg font-semibold">
            {financialProductLabel}
          </Typography>
          <FinanceDisplayBlocks
            displayBlocks={displayBlocks}
            variant={getDisplayBlocksVariant(journey)}
            vehicleQuantity={vehicleQuantity}
            isReadOnly={isReadOnly}
            deal={deal}
          />
        </>
      )}
      {shouldDisplayModalLink ? (
        <Typography
          className="inline-block text-sm text-primaryOrange cursor-pointer underline italic mt-4 hover:text-primaryDark"
          onClick={() => setIsLegalModalOpen(true)}
        >
          {t("basket.financing.legalMention")}
        </Typography>
      ) : (
        parsedLegalMentions.map((paragraph, index) => (
          <Typography className="text-sm" key={index}>
            {paragraph}
          </Typography>
        ))
      )}
      {isWidgetEditable && (
        <>
          <SimpleButton
            className="py-4"
            dataId="financefinancing-openfinancewidget"
            onClick={openFinanceWidget}
          >
            <div className="flex justify-center items-center gap-4">
              <AnimatedTuneIcon
                height="28px"
                width="28px"
                className="text-black"
              />
              <Typography className="text-lg font-semibold uppercase">
                {customizeButtonText}
              </Typography>
              <ArrowRightIcon size="sm" />
            </div>
          </SimpleButton>
          <Typography className="text-sm m-0 mb-8">
            {t("basket.financing.customizeInfo")}
          </Typography>
        </>
      )}
      {ecoBonus.enabled && (
        <Typography className="text-sm mt-4">
          {isB2BSelected
            ? isQuantityVisible && vehicleQuantity > 1
              ? t("price.b2b.multipleVehiclesEcologicalBonus", {
                  ecoBonusPerVehicle: getFormattedPrice(
                    Number(ecoBonus.netAmount)
                  ),
                  quantity: vehicleQuantity,
                  total: getFormattedPrice(
                    Number(
                      priceAlgorithm.times(ecoBonus.netAmount, vehicleQuantity)
                    )
                  ),
                })
              : t("price.b2b.ecologicalBonus", {
                  amount: getFormattedPrice(Number(ecoBonus.netAmount)),
                })
            : t("price.b2c.ecologicalBonus", {
                amount: getFormattedPrice(Number(ecoBonus.netAmount)),
              })}
        </Typography>
      )}

      {withDeliveryInfo && (
        <Typography className="text-sm my-2">
          <Trans i18nKey="price.deliveryInfo.financing" />
        </Typography>
      )}
      <LegalMentionModal
        isOpen={isLegalModalOpen}
        legalMention={legalMention}
        onClose={() => setIsLegalModalOpen(false)}
      />
      {isModifiable && (
        <SimpleButton
          className="font-bold text-primaryDark hover:text-black m-0 p-2"
          dataId="basket-financing-modify-button"
          onClick={onModifyButtonClick}
          withArrow
        >
          {t("general.modify")}
        </SimpleButton>
      )}
    </>
  );
};

FinanceFinancing.propTypes = {
  shouldDisplayError: bool.isRequired,
  financialProductLabel: string.isRequired,
  displayBlocks: arrayOf(object).isRequired,
  legalMention: arrayOf(object).isRequired,
  customizeButtonText: string.isRequired,
  journey: string.isRequired,
  isWidgetEditable: bool,
  isModifiable: bool,
  onModifyButtonClick: func,
  withDeliveryInfo: bool,
  isQuantityVisible: bool,
  vehicleQuantity: number,
  isReadOnly: bool,
  deal: dealType,
};

export default FinanceFinancing;
